import React, { useState } from "react";
import { Input, Alert, Button, Collapse, Popconfirm } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  parseHighlightTabContentForEdit,
  serializeHighlightTabFromEditDraft,
} from "./welcomeHighlightsStructured";
import styles from "./style.module.scss";

/**
 * Edit highlights tab JSON ({ items: [...] } or array-of-items): three text areas per item
 * (critical finding ↔ description, meaning, action). Falls back to a single raw textarea
 * when the payload is invalid JSON or not that shape. Empty `items` uses an info helper + raw JSON.
 */
export default function HighlightMetric25TabEditorPane({
  content,
  onContentChange,
}) {
  const { t } = useTranslation();

  const [structured, setStructured] = useState(() => {
    const parsed = parseHighlightTabContentForEdit(content);
    if (!parsed.ok || !Array.isArray(parsed.rows)) {
      return null;
    }
    return {
      rootDoc: parsed.rootDoc,
      rows: parsed.rows.slice(),
    };
  });

  const rawFallback = structured === null;

  const removeHighlight = (idx) => {
    setStructured((prev) => {
      if (!prev) return prev;
      const nextRoot = JSON.parse(JSON.stringify(prev.rootDoc));
      if (!Array.isArray(nextRoot.items)) nextRoot.items = [];
      if (idx < 0 || idx >= nextRoot.items.length) return prev;
      nextRoot.items.splice(idx, 1);
      const nextRows = prev.rows.filter((_, j) => j !== idx);
      const serialized = serializeHighlightTabFromEditDraft(nextRoot, nextRows);
      onContentChange(serialized);
      return { rootDoc: nextRoot, rows: nextRows };
    });
  };

  if (rawFallback) {
    return (
      <>
        <Alert
          type="warning"
          showIcon
          className={styles.highlightMetric25Alert}
          message={t("welcome.highlightTabEditRawJsonFallback")}
        />
        <Input.TextArea
          className={`${styles.promptEditorTextarea} ${styles.highlightMetric25RawTa}`}
          value={content}
          onChange={(e) => onContentChange(e.target.value)}
          autoSize={{ minRows: 16, maxRows: 42 }}
          spellCheck={false}
        />
      </>
    );
  }

  const itemsMeta = structured.rootDoc.items || [];

  if (structured.rows.length === 0) {
    return (
      <div className={styles.highlightMetric25TabEditor}>
        <Alert
          type="info"
          showIcon
          className={styles.highlightMetric25Alert}
          message={t("welcome.highlightTabEditEmptyTab")}
        />
        <Input.TextArea
          className={`${styles.promptEditorTextarea} ${styles.highlightMetric25RawTa}`}
          value={content}
          onChange={(e) => onContentChange(e.target.value)}
          autoSize={{ minRows: 12, maxRows: 42 }}
          spellCheck={false}
        />
      </div>
    );
  }

  const updateRow = (idx, field, value) => {
    setStructured((prev) => {
      if (!prev) return prev;
      const nextRows = prev.rows.map((r, j) =>
        j === idx ? { ...r, [field]: value } : r,
      );
      const serialized = serializeHighlightTabFromEditDraft(
        prev.rootDoc,
        nextRows,
      );
      onContentChange(serialized);
      return { rootDoc: prev.rootDoc, rows: nextRows };
    });
  };

  return (
    <div className={styles.highlightMetric25TabEditor}>
      <Collapse
        bordered={false}
        defaultActiveKey={structured.rows.map((_, i) => String(i))}
      >
        {structured.rows.map((fields, idx) => {
          const meta = itemsMeta[idx] || {};
          const titleRaw =
            String(meta.title ?? meta.headline ?? meta.tag_label ?? "").trim();
          const header =
            titleRaw ||
            t("welcome.highlightTabEditUntitledChip", {
              index: idx + 1,
            });

          return (
            <Collapse.Panel
              header={
                <div className={styles.highlightMetric25PanelHeader}>
                  <span className={styles.highlightMetric25PanelTitle}>
                    {header}
                  </span>
                  <Popconfirm
                    placement="bottomRight"
                    title={t("welcome.highlightTabRemoveConfirm")}
                    okText={t("common.delete")}
                    okButtonProps={{ danger: true }}
                    cancelText={t("common.cancel")}
                    onConfirm={() => {
                      removeHighlight(idx);
                    }}
                  >
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      aria-label={t("welcome.highlightTabRemove")}
                      title={t("welcome.highlightTabRemove")}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Popconfirm>
                </div>
              }
              key={String(idx)}
            >
              <div className={styles.highlightMetric25Fields}>
                <label className={styles.highlightMetric25Label}>
                  {t("welcome.highlightsDashCriticalTitle")}
                </label>
                <Input.TextArea
                  value={fields.critical}
                  onChange={(e) =>
                    updateRow(idx, "critical", e.target.value)
                  }
                  autoSize={{ minRows: 4, maxRows: 16 }}
                  className={`${styles.promptEditorTextarea} ${styles.highlightMetric25StructuredTa}`}
                />
                <label className={styles.highlightMetric25Label}>
                  {t("welcome.highlightsDashMeaningTitle")}
                </label>
                <Input.TextArea
                  value={fields.meaning}
                  onChange={(e) =>
                    updateRow(idx, "meaning", e.target.value)
                  }
                  autoSize={{ minRows: 3, maxRows: 14 }}
                  className={`${styles.promptEditorTextarea} ${styles.highlightMetric25StructuredTa}`}
                />
                <label className={styles.highlightMetric25Label}>
                  {t("welcome.highlightsDashActionTitle")}
                </label>
                <Input.TextArea
                  value={fields.action}
                  onChange={(e) =>
                    updateRow(idx, "action", e.target.value)
                  }
                  autoSize={{ minRows: 3, maxRows: 14 }}
                  className={`${styles.promptEditorTextarea} ${styles.highlightMetric25StructuredTa}`}
                />
              </div>
            </Collapse.Panel>
          );
        })}
      </Collapse>
    </div>
  );
}
